import { BellIcon, CheckCheckIcon, CircleAlertIcon, ClockIcon, PlayIcon } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { type NotificationItem, getNotifications, markNotificationsRead } from "../lib/api";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

/**
 * Dzwonek powiadomień.
 *
 * Powstał, bo pobranie odcinka trwa minuty, a nikt nie siedzi przy pasku
 * postępu: uruchamiasz, zamykasz kartę i nie masz skąd wiedzieć, kiedy materiał
 * jest gotowy ani czy kaskada się udała.
 *
 * **Odświeżamy przy powrocie do karty, nie w kółko.** Powiadomienia powstają
 * w minutowej skali, więc odpytywanie co dziesięć sekund byłoby ruchem bez
 * treści. Powrót do karty jest dokładnie tym momentem, w którym ktoś chce
 * wiedzieć, co się stało w tle.
 */

/** Ikona zależna od rodzaju: sam tekst na liście czyta się wolniej. */
const KIND_ICONS: Record<NotificationItem["kind"], LucideIcon> = {
  "media-ready": PlayIcon,
  "media-failed": CircleAlertIcon,
  "media-expiring": ClockIcon,
  "episode-new": BellIcon,
};

const KIND_TONE: Record<NotificationItem["kind"], string> = {
  "media-ready": "text-success",
  "media-failed": "text-destructive",
  "media-expiring": "text-warning",
  "episode-new": "text-accent",
};

export function NotificationBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(() => {
    getNotifications()
      .then((data) => {
        setItems(data.items);
        setUnread(data.unread);
      })
      // Cisza przy porażce: dzwonek jest dodatkiem, a komunikat o nieudanym
      // pobraniu listy powiadomień byłby powiadomieniem o powiadomieniach.
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    load();
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", load);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", load);
    };
  }, [load]);

  const readAll = useCallback(() => {
    // Stan lokalny zmieniamy od razu, bez czekania na odpowiedź: oznaczenie jako
    // przeczytane nie ma trybu porażki, który widz musiałby zobaczyć, a plakietka
    // gasnąca po sekundzie wygląda na zacięcie.
    setItems((prev) => prev.map((item) => ({ ...item, read: true })));
    setUnread(0);
    void markNotificationsRead().catch(() => load());
  }, [load]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={unread > 0 ? `Powiadomienia (${unread} nieprzeczytane)` : "Powiadomienia"}
        >
          <BellIcon />
          {unread > 0 && (
            /* Kropka z liczbą, nie sama kropka: „ile" jest tu informacją, a nie
               ozdobą — inaczej nie wiadomo, czy warto rozwijać. */
            <span className="absolute -top-0.5 -right-0.5 flex min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[0.625rem] font-semibold text-primary-foreground tabular-nums">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="max-h-[70svh] w-[min(22rem,90vw)] overflow-y-auto">
        {items.length === 0 ? (
          <p className="px-2 py-3 text-sm text-muted-foreground">
            Nic nowego. Tu trafi informacja, gdy materiał będzie gotowy albo gdy plik zacznie
            wygasać.
          </p>
        ) : (
          <>
            {unread > 0 && (
              <>
                <DropdownMenuItem onSelect={readAll}>
                  <CheckCheckIcon />
                  Oznacz wszystkie jako przeczytane
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}

            {items.map((item) => {
              const Icon = KIND_ICONS[item.kind];
              return (
                <DropdownMenuItem
                  key={item.id}
                  className="items-start gap-2"
                  onSelect={() => {
                    if (item.href !== null) navigate(item.href);
                  }}
                >
                  <Icon className={cn("mt-0.5", KIND_TONE[item.kind])} />
                  <span className="flex min-w-0 flex-col">
                    <span className={cn("truncate", item.read || "font-medium")}>{item.title}</span>
                    {item.body !== null && (
                      <span className="truncate text-xs text-muted-foreground">{item.body}</span>
                    )}
                    <span className="text-[0.625rem] text-muted-foreground">
                      {relativeTime(item.createdAt)}
                    </span>
                  </span>
                </DropdownMenuItem>
              );
            })}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Czas w formie „przed chwilą", „2 godz.".
 *
 * Bez `Intl.RelativeTimeFormat`: przy trzech progach ta funkcja jest krótsza od
 * konfiguracji formatera, a polskie formy, których potrzebujemy, są skrótami
 * („godz.", „dni") i nie odmieniają się.
 */
function relativeTime(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 60_000) return "przed chwilą";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} godz.`;
  return `${Math.floor(hours / 24)} dni`;
}
